import { useCallback, useContext } from "react";
import { AppContext } from "../app/store";
import {
  AnalyticsActionType,
  AnalyticsButton,
  Ga4Event,
  Ga4EventType,
} from "../app/contexts/Analytics/types";
import { updateStates } from "../pages/LandingPage/routingHelpers";
import { useHistory } from "react-router";
import { planTypeGa4EventMap } from "../components/CoverageSelector/constants";
import { hasGlobalSession } from "./loginHelpers";
import {
  AppState,
  CoverageSelectorSelectedValues,
  CoverageSelectorCoverageType,
  Pharmacy,
  PharmacyType,
  PrescriptionDrug,
} from "../@types";
import routes from "../app/routes";
import { hasMailOrderPharmacy } from "../app/store/AppStore";
import { useIsBEDAPDown } from ".";

export type LiCoverageSelectorRoutingParams = {
  pharmacies: Pharmacy[];
  prescriptions: PrescriptionDrug[];
  pharmacyType: PharmacyType;
  isBEDAPDown: boolean;
};

export type CoverageSelectorRoutingParams = {
  isBEDAPDown: boolean;
  isLoggedIn: boolean;
  pharmacies: Pharmacy[];
  prescriptions: PrescriptionDrug[];
  pharmacyType: PharmacyType;
};

/**
 * Determines some of the possible next routes from the coverage selector,
 * based on whether they're LI or anonymous, the status of BEDAP, and the current
 * status of the bene's pharmacies and prescription drugs
 */
export const getNextRouteFromCoverageSelector = ({
  isBEDAPDown,
  isLoggedIn,
  pharmacies,
  prescriptions,
  pharmacyType,
}: CoverageSelectorRoutingParams) => {
  const hasMailOrderPharmacies = hasMailOrderPharmacy({
    pharmacyType,
  } as AppState);
  const pharmacyCount = pharmacies.length + (hasMailOrderPharmacies ? 1 : 0);
  const drugsCount = prescriptions.length;
  const isMissingPharmsOrDrugs = pharmacyCount === 0 || drugsCount === 0;
  const baseRoutes = isLoggedIn ? routes.summary : routes;
  if (!isLoggedIn || isBEDAPDown) {
    return baseRoutes.lisQuestions;
  } else {
    return isMissingPharmsOrDrugs
      ? baseRoutes.drugSearchPreferences
      : baseRoutes.searchResults;
  }
};

/**
 * Provides a map of desination routes for a bene based on chosen coverage type
 */
export const getCoverageSelectorDestinationMap = ({
  isBEDAPDown,
  isLoggedIn,
  pharmacies,
  prescriptions,
  pharmacyType,
}: CoverageSelectorRoutingParams): Record<
  CoverageSelectorCoverageType,
  string
> => {
  const nextRoute = getNextRouteFromCoverageSelector({
    isBEDAPDown,
    isLoggedIn,
    pharmacies,
    prescriptions,
    pharmacyType,
  });
  return {
    [CoverageSelectorCoverageType.MAPD]: nextRoute,
    [CoverageSelectorCoverageType.PDP]: nextRoute,
    [CoverageSelectorCoverageType.PDPAndMedigap]: nextRoute,
    [CoverageSelectorCoverageType.MedigapPolicy]: routes.medigap.landingPage,
    [CoverageSelectorCoverageType.learnMore]: routes.coverageWizard.options,
  };
};

/**
 * Hook that provides a map of desination routes for a bene based on chosen
 * coverage type
 */
export const useCoverageSelectorDestinationMap = () => {
  const {
    state: { pharmacies, pharmacyType, prescriptions },
    state,
  } = useContext(AppContext);
  const isBEDAPDown = useIsBEDAPDown();
  const isLoggedIn = hasGlobalSession(state);
  return getCoverageSelectorDestinationMap({
    isBEDAPDown,
    isLoggedIn,
    pharmacies,
    pharmacyType,
    prescriptions,
  });
};

/**
 * Populate initial coverage selector form values
 */
export const getInitialFormValues = ({
  beneficiary,
  county,
  zipcode,
}: {
  beneficiary: AppState["beneficiary"];
  county: AppState["county"];
  zipcode: AppState["zipcode"];
}) => ({
  zipcode: zipcode || beneficiary?.mailing_address.zipcode,
  fips: county?.fips || beneficiary?.mailing_address.fips_state_county || "",
});

/**
 * easy helpers to add to <CoverageSelector/> or <CoverageSelectorV2/> as props
 */
export function useCoverageSelector() {
  const {
    state: { beneficiary, county, zipcode },
    dispatch,
  } = useContext(AppContext);
  const destinationMap = useCoverageSelectorDestinationMap();
  const history = useHistory();
  const initialFormValues = getInitialFormValues({
    beneficiary,
    county,
    zipcode,
  });
  const onApply = useCallback(
    (
      selectedValues: CoverageSelectorSelectedValues,
      analyticsButton?: AnalyticsButton,
      eventAction?: string
    ) => {
      updateStates(selectedValues, dispatch);

      if (selectedValues.coverageType) {
        const url = window.location.href.split("/?")[0];
        const destination = destinationMap[selectedValues.coverageType];
        const linkUrl = `${url}${destination}${location.search}`;

        if (analyticsButton && eventAction) {
          dispatch({
            type: AnalyticsActionType.SEND_BUTTON_ENGAGEMENT_EVENT,
            settings: {
              button: analyticsButton,
              linkUrl,
            },
          });
          dispatch({
            type: AnalyticsActionType.SEND_GA4_EVENT,
            settings: {
              event_name: Ga4Event.PLAN_FINDER_STARTED,
              event_type: Ga4EventType.UI_INTERACTION,
              link_text: analyticsButton.text,
              link_url: linkUrl,
              mct_plan_type: planTypeGa4EventMap[selectedValues.coverageType],
              mct_plan_year: selectedValues.coverageYear,
              text: analyticsButton.text,
            },
          });
          dispatch({
            type: AnalyticsActionType.SEND_TEALIUM_EVENT,
            settings: {
              event_action: eventAction,
              event_label: planTypeGa4EventMap[selectedValues.coverageType],
            },
          });
        }
        history.push(destination);
      }
    },
    [history]
  );

  return {
    initialFormValues,
    onApply,
  };
}
